<template>
  <loader :is-loading="isUpdating || isDeleting" />
  <thread-item-wrapper
    :comments="log.data.comments"
    disable-comments
    class="note"
  >
    <template #content>
      <div class="flex items-center justify-between gap-x-2">
        <business-name
          :id="log.id"
          class="max-w-fit"
          :name="log.business_legal_name"
          :application-id="log.application_id"
          :type="log.type"
          @click.stop
        />
        <date-time
          v-if="log.data.created_at"
          :date="log.data.created_at"
          show-time-if-today
          class="text-xs"
          data-cy="note-created-at"
        />
      </div>
      <div class="grid grid-cols-4 items-center py-4">
        <div class="col-span-4">
          <text-area
            v-if="isEditing"
            v-model="noteText"
            class="w-full overflow-y-hidden rounded-md"
            :placeholder="$t('COMMON.DESCRIPTION')"
            name="noteText"
            @update:model-value="noteText = $event"
            @blur="updateNote"
          />
          <div
            v-else
            data-cy="note-text"
            class="cursor-text"
            @click="handleClick"
          >
            {{ log.data.comment }}
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <div
        v-if="isEditing"
        class="flex w-full justify-between space-y-1 bg-white"
      >
        <div class="flex-row-full-justify-between wrapper">
          <notes-dropdown
            v-if="log.data.notable_type === FullNotableType.call"
            v-model="callDisposition"
            :options="callStatuses"
            :colors-for-options="COLORS_BY_DISPOSITION_NOTE_STATUS"
            :placeholder="$t('ACTIVITY.SELECT_CALL_DISPOSITION')"
            class="max-w-60 min-w-50"
          />
        </div>
      </div>
    </template>
  </thread-item-wrapper>
</template>
<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { NotableType, NoteType } from "@/enums/dealActivity";
import { FullNotableType } from "@/enums/dealActivity";
import { getEnumKeyFromValue } from "@/helpers/common";
import { COLORS_BY_DISPOSITION_NOTE_STATUS } from "@/helpers/constants/colors";
import { NOTE_UPDATED } from "@/helpers/constants/events";
import { useAuth } from "@/hooks/auth";
import { useClipboardWithMessage } from "@/hooks/clipboard";
import { useEmitter } from "@/hooks/common";
import { useInbox } from "@/hooks/inbox";
import {
  useDeleteNoteMutation,
  useUpdateNoteMutation,
} from "@/hooks/queries/useInbox";

import ThreadItemWrapper from "@/components/communicationLogs/communication/threads/openState/ThreadItemWrapper.vue";
import DateTime from "@/components/ui/ConvertibleDateTime.vue";
import Loader from "@/components/ui/Loader.vue";
import NotesDropdown from "@/components/ui/NotesDropdown.vue";
import TextArea from "@/views/deals/components/dealNotes/AutoresizingTextArea.vue";

import type { NoteLog } from "@/models/communicationLogs";
import type { NotePayload } from "@/models/notes";

const { log } = defineProps<{ log: NoteLog }>();

const { t } = useI18n();
const { getters } = useStore();
const { isSuperAdmin, isAdmin } = useAuth();
const copyToClipboard = useClipboardWithMessage();
const emitter = useEmitter();

const noteText = ref("");
const isEditing = ref(false);
const callDisposition = ref(
  log.data.call_disposition?.replace(/_/g, " ") ??
    t("ACTIVITY.SELECT_CALL_DISPOSITION")
);

const { inboxAllActivityActive } = useInbox();

const callDispositions = computed<string[]>(
  () => getters["options/callDispositions"]
);

const callStatuses = computed(() =>
  Object.entries(callDispositions.value).map(([id, value]) => ({
    id,
    name: value,
  }))
);

const isCurrentUserNote = computed(
  () => getters["auth/user"]?.id === log.data.user?.id
);

const handleClick = () => {
  isEditing.value = true;
};

const copyNoteLink = () => {
  const origin = location?.origin || "";
  const path = `/deals/${log.application_id}/progress`;
  const noteId = log.data.id;

  copyToClipboard(`${origin}${path}?note=${noteId}`);
};

const { mutateAsync: updateNoteMutation, isPending: isUpdating } =
  useUpdateNoteMutation(inboxAllActivityActive.value);
const { mutateAsync: deleteNoteMutation, isPending: isDeleting } =
  useDeleteNoteMutation(inboxAllActivityActive.value);

const updateNote = async () => {
  const { data } = log;

  if (data.comment === noteText.value) {
    isEditing.value = false;
    return;
  }

  const payload: NotePayload = {
    id: data.id,
    notable_type: NotableType[data.notable_type],
    notable_id: data.notable_id,
    comment: noteText.value,
    visibility_scope: data.visibility_scope,
  };
  if (
    log.data.notable_type ===
    getEnumKeyFromValue(NotableType, NoteType.Application)
  ) {
    payload.stage = data.stage;
    payload.visible_in_deal_list = data.visible_in_deal_list;
  }
  if (
    log.data.notable_type === getEnumKeyFromValue(NotableType, NoteType.Call) &&
    data.call_disposition
  ) {
    payload.call_disposition = data.call_disposition;
  }
  if (callDisposition.value !== t("ACTIVITY.SELECT_CALL_DISPOSITION")) {
    payload.call_disposition = callDisposition.value;
  }

  await updateNoteMutation(payload);
  emitter.emit(NOTE_UPDATED);
  isEditing.value = false;
};

const deleteNote = async () => {
  if (
    isCurrentUserNote.value ||
    isSuperAdmin ||
    (isAdmin && getEnumKeyFromValue(NotableType, NoteType.Business))
  ) {
    await deleteNoteMutation(log.data.id);
    emitter.emit(NOTE_UPDATED);
  }
};

watch(
  () => log.data.comment,
  (updatedValue) => (noteText.value = updatedValue),
  { immediate: true }
);

defineExpose({ copyNoteLink, deleteNote });
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.note:deep() {
  @apply border-none shadow-none;
  .log-footer {
    @apply shadow-none;
  }
}
</style>
